import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const MOBILE_BREAKPOINT = 768;

export class ChkTable implements Plugin {
  private readonly expandableRows: NodeListOf<HTMLElement>;
  private readonly boundClickHandlers = new Map<
    HTMLElement,
    (e: Event) => void
  >();
  private readonly boundKeyHandlers = new Map<
    HTMLElement,
    (e: Event) => void
  >();

  constructor(private readonly element: HTMLElement) {
    this.expandableRows = element.querySelectorAll<HTMLElement>(
      "[data-table-expandable-row]",
    );
  }

  init(): void {
    if (this.expandableRows.length === 0) return;

    this.expandableRows.forEach((row) => {
      const clickHandler = (e: Event) => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) return;
        e.preventDefault();
        this.toggle(row);
      };

      const keyHandler = (e: Event) => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) return;
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          e.preventDefault();
          this.toggle(row);
        }
      };

      this.boundClickHandlers.set(row, clickHandler);
      this.boundKeyHandlers.set(row, keyHandler);
      row.addEventListener("click", clickHandler);
      row.addEventListener("keydown", keyHandler);
    });
  }

  destroy(): void {
    this.boundClickHandlers.forEach((handler, row) => {
      row.removeEventListener("click", handler);
    });
    this.boundKeyHandlers.forEach((handler, row) => {
      row.removeEventListener("keydown", handler);
    });
    this.boundClickHandlers.clear();
    this.boundKeyHandlers.clear();
  }

  private toggle(row: HTMLElement): void {
    const controlsId = row.getAttribute("aria-controls");
    if (!controlsId) return;

    const detail = this.element.querySelector<HTMLElement>(`#${controlsId}`);
    if (!detail) return;

    const isExpanded = row.getAttribute("aria-expanded") === "true";

    if (isExpanded) {
      row.setAttribute("aria-expanded", "false");
      detail.classList.remove("is-open");
      detail.setAttribute("aria-hidden", "true");
    } else {
      row.setAttribute("aria-expanded", "true");
      detail.classList.add("is-open");
      detail.setAttribute("aria-hidden", "false");
    }
  }
}

PluginRegistry.register("chkTable", ChkTable);
